import { useEffect, useMemo, useState } from "react";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { useGetWorkbenchCollaborators } from "@vega-api/generated/auth-workbench-v1/auth-workbench-v1";
import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { formatFullName } from "@vega-core/utils/formatString";

import { useCollaboratorRolesManager } from "./useCollaboratorRolesManager";
import { useManageRolesSelection } from "./useManageRolesSelection";

type Step = "edit" | "select";

export const useManageRolesModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const showToast = useToast();
  const [step, setStep] = useState<Step>("select");

  const { data: collaboratorsResponse } = useGetWorkbenchCollaborators();
  const collaborators = useMemo(
    () => collaboratorsResponse?.data ?? [],
    [collaboratorsResponse],
  );

  const selection = useManageRolesSelection({ collaborators });

  const selectedCollaborators = useMemo(
    () =>
      collaborators.filter((collaborator) =>
        selection.selectedUserIds.has(collaborator.id),
      ),
    [collaborators, selection.selectedUserIds],
  );

  const manager = useCollaboratorRolesManager({
    collaborators: selectedCollaborators,
    isEditing: true,
  });

  useEffect(() => {
    if (!open) {
      setStep("select");
      selection.clearSelection();
      manager.resetAll();
    }
  }, [open]);

  const formatFailedNames = (
    failed: { userId: string }[],
    allCollaborators: CollaboratorDetails[],
  ): string =>
    failed
      .map((failure) => {
        const collaborator = allCollaborators.find(
          (entry) => entry.id === failure.userId,
        );

        if (!collaborator) return failure.userId;

        return formatFullName({
          firstName: collaborator.firstName,
          middleName: collaborator.middleName,
          lastName: collaborator.lastName,
        });
      })
      .join(", ");

  const handleSave = () => {
    void manager.save({
      onSuccess: () => {
        showToast({
          title: m.settings_collaborators_manageRoles_saveSuccess({
            count: String(selectedCollaborators.length),
          }),
          variant: "success",
        });
        setOpen(false);
      },
      onPartialFailure: (failed) => {
        showToast({
          title: m.settings_collaborators_manageRoles_savePartialFailure({
            names: formatFailedNames(failed, collaborators),
          }),
          variant: "destructive",
        });
      },
      onError: (error) => {
        const { title, description } = getErrorMessage(error, {
          defaultTitle: m.settings_collaborators_manageRoles_saveError(),
        });
        showToast({ title, description, variant: "destructive" });
      },
    });
  };

  return {
    step,
    setStep,
    isSelectStep: step === "select",
    collaborators,
    selectedCollaborators,
    selection,
    manager,
    handleSave,
  };
};
